import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SectionHead from "@/components/SectionHead";
import FinalCTA from "@/components/FinalCTA";
import { useConnectModal } from "@/context/ConnectModalContext";
import ScrollRail from "@/components/ScrollRail";
import { ReachRail } from "@/components/Rails";
import { DelhiSkylineSVG, MumbaiSkylineSVG, StructuralDiagramSVG, ArchGridSVG } from "@/components/CityArchSVG";
import { ARMS } from "@/lib/constants";
import { useEffect, useRef, useState } from "react";

/* ---- Utility: reveal on scroll ---- */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.06 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ================================================================
   HERO — light, full-bleed with architectural photography
   ================================================================ */
const Hero = () => {
  const { openConnectModal } = useConnectModal();
  const [ref, visible] = useReveal();

  return (
    <section className="hp-hero-light hp-immersive" data-testid="hero-section" style={{ minHeight: "88vh" }}>
      <img 
        src="/images/light_modern_architecture_1791101259356.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center 40%' }}
        alt="Modern architecture" 
      />
      
      {/* Light gradient for text readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 45%, rgba(255,255,255,0.2) 100%)",
        }}
      />

      <div className="hop-container relative z-10 py-24 md:py-36 w-full" ref={ref}>
        <div
          className={`max-w-4xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
        >
          {/* Tri-colour accent bar */}
          <div className="hp-rule mb-8 w-16" aria-hidden="true" />

          <p
            className="text-xs font-bold tracking-[0.22em] uppercase mb-6"
            style={{ color: "var(--hop-blue-soft)" }}
          >
            Business Consultancy · New Delhi
          </p>

          <h1 className="hp-h1" style={{ color: "var(--hop-blue)", maxWidth: "18ch" }}>
            The infrastructure behind your growth.
          </h1>

          <p className="hp-lead mt-7" style={{ color: "#3c4768", maxWidth: "54ch" }}>
            House of Projects enters your business, diagnoses what is constraining it,
            and builds the systems, visibility and talent infrastructure required to scale.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={openConnectModal}
              className="hp-btn hp-btn-primary"
              data-testid="hero-cta"
            >
              Request a conversation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
            <Link to="/how-we-work" className="hp-btn hp-btn-ghost">
              How we work
            </Link>
          </div>
        </div>

        {/* Three arms — minimal label strip */}
        <div
          className={`mt-16 md:mt-20 grid grid-cols-3 gap-0 max-w-lg transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ borderTop: "1px solid rgba(10,23,63,0.15)" }}
        >
          {[
            ["01", "Technology & Systems"],
            ["02", "Visibility"],
            ["03", "Talent"],
          ].map(([n, l], i) => (
            <div
              key={n}
              className="py-5"
              style={{
                borderRight: i < 2 ? "1px solid rgba(10,23,63,0.15)" : "none",
                paddingLeft: i > 0 ? "1.5rem" : 0,
                paddingRight: i < 2 ? "1.5rem" : 0,
              }}
            >
              <span
                className="block font-mono text-[10px] font-bold tracking-[0.18em] mb-1"
                style={{ color: "var(--hop-blue-soft)", opacity: 0.7 }}
              >
                {n}
              </span>
              <span className="text-[13px] font-semibold" style={{ color: "var(--hop-blue)" }}>
                {l}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   CITY SECTION — Delhi
   Full-bleed with gradient overlay and editorial copy (Light Theme)
   ================================================================ */
const DelhiSection = () => {
  const [ref, visible] = useReveal();

  return (
    <section
      ref={ref}
      className="hp-immersive-light"
      style={{ minHeight: "62vh" }}
      aria-label="Delhi — where businesses are built"
    >
      <img 
        src="/images/abstract_business_structure_1791101291744.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center center' }}
        alt="Abstract business structure" 
      />

      {/* Left gradient for text readability (Light) */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.75) 50%, rgba(255,255,255,0.15) 100%)",
        }}
      />

      <div className="hop-container relative z-10 py-20 md:py-28 w-full">
        <div
          className={`max-w-xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
        >
          <p className="hp-eyebrow hp-eyebrow-bar" style={{ color: "var(--hop-blue-soft)" }}>
            Delhi
          </p>
          <h2
            className="hp-immersive-h"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.4rem)", color: "var(--hop-blue)" }}
          >
            Where businesses are built.
          </h2>
          <p className="hp-immersive-lead" style={{ color: "#3c4768" }}>
            House of Projects works from the ground up — across technology, visibility
            and talent infrastructure — to build the operating layer behind growing companies.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/what-we-do" className="hp-btn hp-btn-primary text-sm">
              Explore capabilities <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   THREE DIVISIONS — consulting-style numbered system
   ================================================================ */
const ArmDivisions = () => {
  const [ref, visible] = useReveal();

  return (
    <section
      className="hp-section hp-surface-stone hp-grain-light"
      aria-labelledby="arms-heading"
      ref={ref}
    >
      <div className="hop-container">
        <div
          className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          <p className="hp-label text-[var(--hop-brand)]">One platform. Three capabilities.</p>
          <h2 id="arms-heading" className="hp-h2 mt-3 max-w-2xl">
            Systems. Visibility. Talent.
          </h2>
          <p className="hp-lead mt-5 max-w-2xl">
            Each division stands alone. Together, they cover every dimension of how a business
            sells, becomes known, hires and operates.
          </p>
        </div>

        <div
          className={`mt-14 grid grid-cols-1 lg:grid-cols-3 gap-0 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ borderTop: "1px solid #d8dce5" }}
          data-testid="arm-cards"
        >
          {ARMS.map((a, i) => (
            <div
              key={a.key}
              data-arm={a.key}
              className="hp-division"
              style={{
                borderTopColor: a.key === "hop" ? "var(--arm-hop)" : a.key === "ae" ? "var(--arm-ae)" : "var(--arm-ak)",
                paddingLeft: i > 0 ? "2rem" : 0,
                paddingRight: i < 2 ? "2rem" : 0,
                borderLeft: i > 0 ? "1px solid #d8dce5" : "none",
                "--c": a.key === "hop" ? "var(--arm-hop)" : a.key === "ae" ? "var(--arm-ae)" : "var(--arm-ak)",
              }}
            >
              <span className="hp-division-num">0{i + 1}</span>
              <h3 className="hp-division-name">{a.name}</h3>
              <p className="hp-division-descriptor">{a.descriptor}</p>
              <p className="hp-division-body">{a.problem}</p>

              <dl className="mt-5 space-y-3 text-[13.5px]">
                <div>
                  <dt className="font-bold text-[11px] tracking-wider uppercase text-slate-400 mb-0.5">Capability</dt>
                  <dd className="text-slate-600 leading-snug">{a.capability}</dd>
                </div>
                <div>
                  <dt className="font-bold text-[11px] tracking-wider uppercase text-slate-400 mb-0.5">Outcome</dt>
                  <dd className="text-slate-600 leading-snug">{a.outcome}</dd>
                </div>
              </dl>

              <div className="mt-6">
                {a.external ? (
                  <a
                    href={a.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hp-link hp-arm-accent text-[14px]"
                    aria-label={`${a.cta} (opens in a new tab)`}
                  >
                    {a.cta} <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                  </a>
                ) : (
                  <Link to={a.to} className="hp-link hp-arm-accent text-[14px]">
                    {a.cta} <ArrowRight className="arrow w-4 h-4" aria-hidden="true" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   COMPARE SECTION — why us, with consulting-style layout
   ================================================================ */
const COMPARE = [
  ["Big-firm consultancies", "Advise, then hand over a deck", "Large teams, high retainers", "Generic playbooks"],
  ["House of Projects", "Advise and execute, same team", "Lean team, lower cost", "Built for Indian markets and 2026 channels"],
];

const Compare = () => (
  <section className="hp-section" aria-labelledby="cmp">
    <div className="hop-container">
      <SectionHead
        label="Why us"
        title={<span id="cmp">Not a bigger firm. A more useful one.</span>}
        lead="Large consultancies are built for the boardroom. We are built to ship campaigns, platforms and hires, and to be accountable for what happens after."
      />

      <Reveal>
        <div className="mt-10 overflow-x-auto hidden md:block">
          <table className="w-full min-w-[560px] text-left text-[15px]">
            <thead>
              <tr style={{ borderBottom: "2px solid #d1d8e8" }}>
                <th className="py-4 pr-4 text-[11px] font-bold tracking-widest uppercase text-slate-400" />
                <th className="py-4 pr-4 text-[11px] font-bold tracking-widest uppercase text-slate-400">Approach</th>
                <th className="py-4 pr-4 text-[11px] font-bold tracking-widest uppercase text-slate-400">Cost</th>
                <th className="py-4 text-[11px] font-bold tracking-widest uppercase text-slate-400">Fit</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE.map((r, i) => (
                <tr
                  key={r[0]}
                  style={{ borderBottom: "1px solid #e6ebf1" }}
                  className={i ? "font-semibold text-hop-blue" : "text-slate-500"}
                >
                  {r.map((c, j) => (
                    <td
                      key={c}
                      className="py-5 pr-4"
                      style={{ fontSize: j === 0 ? "14px" : "15px" }}
                    >
                      {j === 0 && i === 1 ? (
                        <span
                          className="inline-block px-2 py-0.5 rounded text-[12px] font-bold tracking-wide"
                          style={{ background: "var(--hop-brand-tint)", color: "var(--hop-brand)" }}
                        >
                          {c}
                        </span>
                      ) : c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>

      {/* Mobile cards */}
      <div className="mt-8 grid gap-4 md:hidden">
        {COMPARE.map((r, i) => (
          <div
            key={r[0]}
            className={`rounded-sm border p-5 ${i ? "border-[var(--hop-brand)] bg-white shadow-sm" : "border-slate-200 bg-slate-50"}`}
          >
            <p className={`font-display text-[20px] ${i ? "text-hop-blue" : "text-slate-500"}`}>{r[0]}</p>
            <dl className="mt-3 space-y-3 text-[14.5px]">
              {["Approach", "Cost", "Fit"].map((k, j) => (
                <div key={k} className="grid grid-cols-[72px_1fr] gap-3">
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-0.5">{k}</dt>
                  <dd className={i ? "font-semibold text-hop-blue" : "text-slate-600"}>{r[j + 1]}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ================================================================
   MUMBAI SECTION — scale city metaphor (Light Theme)
   ================================================================ */
const MumbaiSection = () => {
  const [ref, visible] = useReveal();

  return (
    <section
      ref={ref}
      className="hp-immersive-light"
      style={{ minHeight: "58vh" }}
      aria-label="Mumbai — scale demands infrastructure"
    >
      <img 
        src="/images/consulting_team_collaboration_1791101279141.jpg" 
        className="hp-immersive-img"
        style={{ objectPosition: 'center center' }}
        alt="Consulting team collaboration" 
      />

      {/* Right-side gradient overlay (Light) */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.7) 55%, rgba(255,255,255,0.15) 100%)",
        }}
      />

      <div className="hop-container relative z-10 py-20 md:py-28 w-full">
        <div
          className={`max-w-2xl transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
        >
          <p className="hp-eyebrow hp-eyebrow-bar" style={{ color: "var(--hop-blue-soft)" }}>
            Scale & Team
          </p>
          <h2
            className="hp-immersive-h"
            style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)", color: "var(--hop-blue)" }}
          >
            Scale is not just about growing.
            <br />
            <span style={{ color: "var(--hop-blue-soft)", opacity: 0.8 }}>
              It is about building what can handle the growth.
            </span>
          </h2>
          <p className="hp-immersive-lead mt-6" style={{ color: "#3c4768" }}>
            We design the operational infrastructure — the systems, the visibility architecture,
            the talent pipeline — that allows companies to scale without breaking.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/how-we-work" className="hp-btn hp-btn-primary text-sm">
              How we work <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link to="/what-we-do" className="hp-btn hp-btn-ghost text-sm">
              See capabilities
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   TRACK RECORD — consulting stat layout
   ================================================================ */
const PROOF = [
  ["5.7M+", "views in 3.5 months for one founder-led Instagram account"],
  ["1M+", "views in 3 months, with 30+ inbound leads, on one long-form YouTube channel"],
  ["50+", "content pieces written, scripted and edited every month across our clients"],
];

const Track = () => (
  <section className="hp-section hp-ink" aria-labelledby="track">
    <div className="hop-container">
      <SectionHead
        dark
        label="Track record"
        title={<span id="track">Results in numbers. Clients stay private.</span>}
        lead="We work under NDA. Nobody will know your personal brand is managed by us, so we share numbers, not names."
      />
      <div className="mt-14">
        <ScrollRail inline={false} dark rail={(p) => <ReachRail p={p} />}>
          {PROOF.map(([n, d], i) => (
            <Link key={n} to="/arise-and-echo#proof" className="hp-beat block mb-12 group">
              <span className="hp-beat-n text-sky-300">0{i + 1}</span>
              <div className="font-display text-5xl md:text-6xl font-medium text-white hp-stat-num mt-2">
                {n}
              </div>
              <p className="mt-3 text-[17px] text-slate-300 leading-relaxed max-w-md">{d}</p>
              <span className="hp-link text-sky-300 mt-4 inline-flex hp-link-u">See the proof</span>
            </Link>
          ))}
        </ScrollRail>
      </div>
      <p className="mt-10 text-sm text-slate-400">
        Results come from individual engagements and are not a guarantee of future outcomes.
      </p>
    </div>
  </section>
);

/* ================================================================
   OPERATING LOOP — consulting process numbers
   ================================================================ */
const LOOP = [
  ["01", "Research", "We enter the business, review how it sells, hires and operates, and build a picture of where value is leaking or constrained."],
  ["02", "Diagnose", "We identify the specific bottleneck — a broken system, a visibility gap, a talent problem — before proposing anything."],
  ["03", "Build", "We deploy the capability that addresses the constraint: a platform, a content operation, a hiring pipeline."],
  ["04", "Optimise", "We measure, learn from what the work produces, and refine the operating layer as the business changes."],
];

const OperatingLoop = () => {
  const [ref, visible] = useReveal();
  return (
    <section className="hp-section hp-surface-stone" aria-labelledby="loop-heading" ref={ref}>
      <div className="hop-container">
        <div
          className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
        >
          <p className="hp-label text-[var(--hop-brand)]">How we work</p>
          <h2 id="loop-heading" className="hp-h2 mt-3 max-w-xl">
            Research. Diagnose. Build. Optimise.
          </h2>
        </div>

        <div
          className={`mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-0 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={{ borderTop: "1px solid #d2d8e2" }}
        >
          {LOOP.map(([num, title, body]) => (
            <div
              key={num}
              className="py-7"
              style={{
                borderBottom: "none",
                paddingLeft: num !== "01" ? "2rem" : 0,
                paddingRight: num !== "04" ? "2rem" : 0,
                borderLeft: num !== "01" ? "1px solid #d2d8e2" : "none",
              }}
            >
              <span
                className="font-display text-[13px] font-bold tracking-widest block mb-3"
                style={{ color: "rgba(38,93,244,0.55)" }}
              >
                {num}
                <span className="ml-2 w-8 inline-block h-[1px] bg-[var(--hop-brand)] opacity-40 align-middle" />
              </span>
              <h3 className="font-display text-[1.15rem] font-semibold text-hop-blue leading-snug">
                {title}
              </h3>
              <p className="mt-2 text-[13.5px] text-slate-500 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/how-we-work" className="hp-link text-[var(--hop-brand)] text-[14px]">
            Read how it works in full <ArrowRight className="arrow w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ================================================================
   HOME PAGE
   ================================================================ */
export default function Home() {
  return (
    <>
      <Seo
        title="Business Consultancy: Technology, Visibility & Talent"
        description="House of Projects is a business infrastructure consultancy helping companies scale through technology systems, visibility infrastructure and talent acquisition."
        path="/"
      />
      <Hero />
      <DelhiSection />
      <ArmDivisions />
      <Compare />
      <OperatingLoop />
      <MumbaiSection />
      <Track />
      <FinalCTA />
    </>
  );
}
